import { Trophy } from 'lucide-react-native';
import { useMemo } from 'react';
import { ScrollView, type TextStyle } from 'react-native';
import { FadeIn } from '../../../components/animations';
import { Box } from '../../../components/primitives/Box';
import { Grid } from '../../../components/primitives/Grid';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { type DateRange, getDateRange, type TimeRange } from '../hooks/useAnalyticsData';
import {
  type AgentLeadCoverage,
  type AgentPerformanceItem,
  useAgentPerformance,
  useResponseTime,
  useUntouchedLeads,
} from '../hooks/useTeamAnalytics';
import { countValue, durationDays, durationMinutes, percentValue } from '../utils/metricDisplay';
import { AlertBanner } from './AlertBanner';
import { TabEmptyState, TabErrorState, TabLoadingState } from './AnalyticsLoadingState';
import { AnalyticsMetricCard } from './AnalyticsMetricCard';
import { ChartCard } from './ChartCard';

interface TeamTabProps {
  timeRange: TimeRange;
}

const LEADERBOARD_MIN_WIDTH = 760;
const numericTextStyle: TextStyle = { fontVariant: ['tabular-nums'] };

// `key` names the column's label under `analytics.team.leaderboard.columns`,
// and carries the width it used to take from its index in a bare label array.
const LEADERBOARD_COLUMNS = [
  { key: 'rank', flex: 0.5 },
  { key: 'agent', flex: 2 },
  { key: 'assigned', flex: 1 },
  { key: 'won', flex: 1 },
  { key: 'conversion', flex: 1 },
  { key: 'cycle', flex: 1 },
] as const;

export function TeamTab({ timeRange }: Readonly<TeamTabProps>) {
  const { currentOrganization } = useAuth();
  const { t } = useTranslation();
  const orgId = currentOrganization?.id;
  const dateRange: DateRange = useMemo(() => getDateRange(timeRange), [timeRange]);

  const { data: responseTime, isLoading, isError, refetch } = useResponseTime(orgId, dateRange);
  const { data: untouched } = useUntouchedLeads(orgId);
  const { data: performance } = useAgentPerformance(orgId, dateRange);
  const agents = performance?.agents;
  const coverage = performance?.coverage;

  if (isLoading) return <TabLoadingState cardCount={2} />;
  if (isError) return <TabErrorState onRetry={refetch} />;

  return (
    <VStack spacing="xl">
      <FadeIn delay={100}>
        <Grid columns={{ mobile: 1, tablet: 2, desktop: 2 }} spacing="xl">
          <AnalyticsMetricCard
            title={t('analytics.team.metrics.avgResponseTime')}
            value={durationMinutes(responseTime?.avgMinutes)}
            subtitle={
              responseTime
                ? t('analytics.team.metrics.responseTimeCoverage', {
                    value: durationMinutes(responseTime.medianMinutes),
                    responded: responseTime.respondedLeads,
                    total: responseTime.totalLeads,
                  })
                : t('analytics.team.metrics.responseTimeFallback')
            }
            metricType="visitors"
            index={0}
          />
          <AnalyticsMetricCard
            title={t('analytics.team.metrics.untouchedLeads')}
            value={countValue(untouched?.count)}
            subtitle={
              untouched
                ? t('analytics.team.metrics.untouchedSubtitle', {
                    percentage: percentValue(untouched.percentage, 1),
                    days: untouched.gracePeriodDays,
                  })
                : t('analytics.team.metrics.untouchedFallback')
            }
            metricType="campaigns"
            index={1}
          />
        </Grid>
      </FadeIn>

      {untouched && untouched.count !== null && untouched.count > 0 && (
        <AlertBanner
          message={t('analytics.team.untouchedAlert', {
            count: untouched.count,
            days: untouched.gracePeriodDays,
          })}
          delay={200}
        />
      )}

      {agents && agents.length > 0 ? (
        <FadeIn delay={300}>
          <ChartCard
            title={t('analytics.team.leaderboard.title')}
            subtitle={
              agents.length > 1
                ? t('analytics.team.leaderboard.subtitle')
                : t('analytics.team.leaderboard.subtitleSingle')
            }
            accentColor="cyan"
            delay={350}
          >
            <AgentLeaderboard agents={agents} coverage={coverage} />
          </ChartCard>
        </FadeIn>
      ) : (
        <VStack spacing="sm">
          <TabEmptyState message={t('analytics.team.leaderboard.empty')} />
          <CoverageNote coverage={coverage} />
        </VStack>
      )}
    </VStack>
  );
}

// The leaderboard has one row per assignee, so a lead nobody owns has no row to
// appear in. Saying so beats letting a manager read a partial board as the whole
// pipeline. An "Unassigned" row would instead earn a rank and a medal for a
// bucket that is not a person.
function CoverageNote({ coverage }: Readonly<{ coverage?: AgentLeadCoverage }>) {
  const { t } = useTranslation();

  if (!coverage || coverage.unassignedLeads <= 0) return null;

  const { totalLeads, assignedLeads, unassignedLeads } = coverage;

  return (
    <Text testID="leaderboard-coverage-note" variant="caption" color={colors.text.tertiary}>
      {t('analytics.team.leaderboard.coverageNote', {
        assigned: assignedLeads,
        total: totalLeads,
        count: unassignedLeads,
      })}
    </Text>
  );
}

function conversionRateColor(rate: number | null): string {
  if (rate === null) return colors.text.tertiary;
  if (rate >= 30) return colors.status.success.main;
  if (rate >= 15) return colors.status.warning.main;
  return colors.text.secondary;
}

interface AgentLeaderboardProps {
  agents: AgentPerformanceItem[];
  coverage?: AgentLeadCoverage;
}

function AgentLeaderboard({ agents, coverage }: Readonly<AgentLeaderboardProps>) {
  const { t } = useTranslation();
  const sorted = [...agents].sort((a, b) => (b.conversionRate ?? -1) - (a.conversionRate ?? -1));
  // A ranking of one is not a ranking, and neither is a board where the ranked
  // metric is zero or unknown for everyone. Medals there celebrate a non-result.
  const showMedals = sorted.length > 1 && sorted.some((a) => (a.conversionRate ?? 0) > 0);

  return (
    <VStack spacing="sm">
      <CoverageNote coverage={coverage} />
      <ScrollView
        testID="agent-leaderboard-scroll"
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <Box
          testID="agent-leaderboard-content"
          style={{ minWidth: LEADERBOARD_MIN_WIDTH, flexGrow: 1 }}
        >
          <HStack
            spacing="sm"
            style={{
              paddingVertical: 10,
              paddingHorizontal: 12,
              borderBottomWidth: 1,
              borderBottomColor: colors.border.subtle,
            }}
          >
            {LEADERBOARD_COLUMNS.map((column) => (
              <Box key={column.key} style={{ flex: column.flex }}>
                <Text
                  variant="caption"
                  color={colors.text.tertiary}
                  style={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    fontWeight: '600',
                    fontSize: 10,
                  }}
                >
                  {t(`analytics.team.leaderboard.columns.${column.key}`)}
                </Text>
              </Box>
            ))}
          </HStack>

          {sorted.map((agent, index) => (
            <FadeIn key={agent.userId} delay={index * 60}>
              <HStack
                spacing="sm"
                align="center"
                style={{
                  paddingVertical: 12,
                  paddingHorizontal: 12,
                  backgroundColor: index % 2 === 0 ? colors.surface.row : 'transparent',
                  borderBottomWidth: 1,
                  borderBottomColor: colors.border.subtle,
                }}
              >
                <Box style={{ flex: 0.5 }}>
                  <HStack spacing="xs" align="center">
                    {showMedals && index < 3 && (
                      <Trophy
                        size={12}
                        color={
                          index === 0
                            ? colors.accent.amber.neon
                            : index === 1
                              ? colors.neutral[300]
                              : colors.accent.amber[700]
                        }
                        strokeWidth={2.5}
                      />
                    )}
                    <Text
                      testID={`agent-leaderboard-rank-${agent.userId}`}
                      variant="body"
                      color={showMedals && index < 3 ? colors.text.primary : colors.text.secondary}
                      style={{ fontWeight: showMedals && index < 3 ? '700' : '400' }}
                    >
                      {index + 1}
                    </Text>
                  </HStack>
                </Box>
                <Box style={{ flex: 2, minWidth: 0 }}>
                  <Text
                    testID={`agent-leaderboard-name-${agent.userId}`}
                    variant="body"
                    color={colors.text.primary}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={{ fontWeight: '500' }}
                  >
                    {agent.userName}
                  </Text>
                </Box>
                <Box style={{ flex: 1 }}>
                  <Text variant="body" color={colors.text.secondary} style={numericTextStyle}>
                    {agent.leadsAssigned}
                  </Text>
                </Box>
                <Box style={{ flex: 1 }}>
                  <Text variant="body" color={colors.text.secondary} style={numericTextStyle}>
                    {agent.leadsConverted}
                  </Text>
                </Box>
                <Box style={{ flex: 1 }}>
                  <Text
                    testID={`agent-leaderboard-conv-${agent.userId}`}
                    variant="body"
                    color={conversionRateColor(agent.conversionRate)}
                    style={[numericTextStyle, { fontWeight: '600' }]}
                  >
                    {percentValue(agent.conversionRate, 1)}
                  </Text>
                </Box>
                <Box style={{ flex: 1 }}>
                  <Text
                    testID={`agent-leaderboard-cycle-${agent.userId}`}
                    variant="body"
                    color={colors.text.secondary}
                    style={numericTextStyle}
                  >
                    {durationDays(agent.avgCycleTimeDays)}
                  </Text>
                </Box>
              </HStack>
            </FadeIn>
          ))}
        </Box>
      </ScrollView>
    </VStack>
  );
}
